import { dollarsInputToCents } from '../../addresses/addressForm/money';
import type { DeliveryType } from '../types';
import type {
  CanonicalDetails,
  CanonicalPallet,
  CanonicalStock,
  PalletRow,
  WarehouseState,
} from './types';

/*
 * Canonical builders. The product form saves in three independent sections and
 * only PUTs the ones that changed, so every dirty check is a comparison between
 * "what the form holds" and "what the server returned". Those two shapes do not
 * line up on their own — one side is strings out of `<input>`s, the other is
 * numbers out of Prisma — so both are funnelled through these builders first.
 *
 * Two properties matter and both are load-bearing for `deepEq`:
 *   - field order is fixed, because equality is `JSON.stringify` on the result
 *   - collections are sorted, because neither the API nor the form guarantees order
 */

export const safeArr = (v: unknown): any[] => (Array.isArray(v) ? v : []);

export const normDtype = (v: unknown): DeliveryType =>
  v === 'PALLET' || v === 'BULK' ? v : 'BULK';

export const asInt = (v: unknown): number | null => {
  const n = parseInt(String(v), 10);
  return Number.isInteger(n) ? n : null;
};

export const canonDetails = (
  name?: string,
  sku?: string,
  description?: string,
  deliveryType?: string,
  trackInventory?: boolean,
  defaultLocationId?: string | null
): CanonicalDetails => ({
  name: name?.trim() || '',
  sku: (sku ?? '').trim(),
  description: (description ?? '').trim(),
  deliveryType: normDtype(deliveryType),
  // Absent means on: the server default is true, so only an explicit false is off.
  trackInventory: trackInventory !== false,
  defaultLocationId: defaultLocationId || null,
});

export const canonStocksFromState = (whState: WarehouseState): CanonicalStock[] => {
  const entries = Object.entries(whState || {}).flatMap(([locationId, s]) =>
    s?.enabled
      ? [
          {
            locationId,
            quantity: asInt(s.quantity) ?? 0,
            stonePriceCents: dollarsInputToCents(s.stonePrice) || null,
          },
        ]
      : []
  );

  entries.sort((a, b) => a.locationId.localeCompare(b.locationId));
  return entries;
};

export const canonStocksFromProduct = (product: any): CanonicalStock[] => {
  const rows = safeArr(product?.stocks).map((s) => ({
    locationId: s.locationId,
    quantity: asInt(s.quantity) ?? 0,
    stonePriceCents: s.stonePriceCents ?? null,
  }));

  rows.sort((a, b) => a.locationId.localeCompare(b.locationId));
  return rows;
};

const byPalletShape = (a: CanonicalPallet, b: CanonicalPallet) =>
  (a.palletTemplateId || '').localeCompare(b.palletTemplateId || '') ||
  (a.name || '').localeCompare(b.name || '') ||
  (a.type || '').localeCompare(b.type || '') ||
  (a.lengthIn ?? 0) - (b.lengthIn ?? 0) ||
  (a.widthIn ?? 0) - (b.widthIn ?? 0) ||
  (a.heightIn ?? 0) - (b.heightIn ?? 0) ||
  (a.weightTons ?? 0) - (b.weightTons ?? 0);

/**
 * Rows missing any of length/width/height are dropped, not defaulted — a
 * half-filled row is a row the user has not finished, and the API requires all
 * three.
 */
export const canonPalletsFromRows = (rows: unknown): CanonicalPallet[] => {
  const out = safeArr(rows)
    .map((r: PalletRow) => ({
      palletTemplateId: r.id,
      name: (r.name ?? '').trim(),
      type: (r.type ?? '').trim(),
      lengthIn: asInt(r.lengthIn),
      widthIn: asInt(r.widthIn),
      heightIn: asInt(r.heightIn),
      weightTons: r.weightTons === '' || r.weightTons == null ? null : Number(r.weightTons),
    }))
    .filter((ps) => ps.lengthIn != null && ps.widthIn != null && ps.heightIn != null);

  out.sort(byPalletShape);
  return out;
};

/**
 * Field order and key set must match `canonPalletsFromRows` exactly, because
 * `deepEq` is `JSON.stringify`.
 */
export const canonPalletsFromProduct = (product: any): CanonicalPallet[] => {
  const out = safeArr(product?.palletSizes).map((ps) => ({
    palletTemplateId: ps.palletTemplateId,
    name: (ps.template?.name ?? '').trim(),
    type: (ps.template?.type ?? '').trim(),
    lengthIn: asInt(ps.template?.lengthIn),
    widthIn: asInt(ps.template?.widthIn),
    heightIn: asInt(ps.template?.heightIn),
    weightTons: ps.template?.weightTons == null ? null : Number(ps.template?.weightTons),
  }));

  out.sort(byPalletShape);
  return out;
};

/** Deep equality via JSON on canonical structures. */
export const deepEq = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
